import { CjsFormat } from "../../format/CjsFormat.js";
import {
    DEFAULT_VALUES,
    OUTPUT_JSON,
    OUTPUT_RAW,
    OUTPUT_VIDEO,
    inspectWithValues,
    probeSupportWithValues,
    isWebM,
    normalizeValues,
    readWithValues,
    toBytes,
    toJsonValue
} from "./core/helpers.js";

const FORMAT_NAME = "CjsWebmFormat";

/**
 * WebM container format profile that inspects EBML segment and track
 * structure and emits raw bytes, debug JSON, or a container-only video
 * payload with codec and duration summaries but no frame decoding.
 */
export class CjsWebmFormat extends CjsFormat
{
    #values = DEFAULT_VALUES;

    /**
     * Create a reusable WebM format profile.
     *
     * @param {object} [options] Default read/inspect options.
     */
    constructor(options = {})
    {
        super();
        this.SetValues(options);
    }

    /**
     * Merge options into this profile.
     *
     * @param {object} [options] Values to merge.
     * @returns {CjsWebmFormat} This format profile.
     */
    SetValues(options = {})
    {
        this.#values = normalizeValues(this.#values, { inputType: "webm", ...options }, FORMAT_NAME);
        return this;
    }

    /**
     * Get normalized profile values with optional per-call overrides.
     *
     * @param {object} [options] Per-call values.
     * @returns {object} Normalized read values.
     */
    GetValues(options = {})
    {
        return normalizeValues(this.#values, { inputType: "webm", ...options }, FORMAT_NAME);
    }

    /**
     * Read WebM bytes with this profile.
     *
     * @param {Uint8Array|ArrayBuffer|DataView} input WebM bytes.
     * @param {object} [options] Per-call values.
     * @returns {object} GPU-free raw/debug payload for the selected emit target.
     */
    Read(input, options = {})
    {
        return readWithValues(input, this.GetValues(options), "webm");
    }

    /**
     * Read WebM bytes asynchronously with this profile.
     *
     * @param {Uint8Array|ArrayBuffer|DataView} input WebM bytes.
     * @param {object} [options] Per-call values.
     * @returns {Promise<object>} GPU-free raw/debug payload for the selected emit target.
     */
    async ReadAsync(input, options = {})
    {
        return this.Read(input, options);
    }

    /**
     * Inspect WebM bytes without decoding media samples.
     *
     * @param {Uint8Array|ArrayBuffer|DataView} input WebM bytes.
     * @param {object} [options] Per-call values.
     * @returns {object} WebM container metadata.
     */
    Inspect(input, options = {})
    {
        return inspectWithValues(input, this.GetValues(options), "webm");
    }

    /**
     * Convert format output into JSON-compatible debug data.
     *
     * @param {any} value Format output.
     * @returns {any} JSON-compatible value.
     */
    ToJSON(value)
    {
        return toJsonValue(value);
    }

    /**
     * One-shot WebM read.
     *
     * @param {Uint8Array|ArrayBuffer|DataView} input WebM bytes.
     * @param {object} [options] Read options.
     * @returns {object} GPU-free raw/debug payload for the selected emit target.
     */
    static read(input, options = {})
    {
        return readWithValues(input, normalizeValues(DEFAULT_VALUES, { inputType: "webm", ...options }, FORMAT_NAME), "webm");
    }

    /**
     * One-shot asynchronous WebM read.
     *
     * @param {Uint8Array|ArrayBuffer|DataView} input WebM bytes.
     * @param {object} [options] Read options.
     * @returns {Promise<object>} GPU-free raw/debug payload for the selected emit target.
     */
    static async readAsync(input, options = {})
    {
        return CjsWebmFormat.read(input, options);
    }

    /**
     * One-shot WebM inspection.
     *
     * @param {Uint8Array|ArrayBuffer|DataView} input WebM bytes.
     * @param {object} [options] Inspect options.
     * @returns {object} WebM container metadata.
     */
    static inspect(input, options = {})
    {
        return inspectWithValues(input, normalizeValues(DEFAULT_VALUES, { inputType: "webm", ...options }, FORMAT_NAME), "webm");
    }

    /**
     * One-shot WebM support probe.
     *
     * @param {Uint8Array|ArrayBuffer|DataView} input WebM bytes.
     * @param {object} [options] Probe options.
     * @returns {object} Support/probe report.
     */
    static probeSupport(input, options = {})
    {
        return probeSupportWithValues(input, normalizeValues(DEFAULT_VALUES, { inputType: "webm", ...options }, FORMAT_NAME), "webm");
    }

    /**
     * Convert format output into JSON-compatible debug data.
     *
     * @param {any} value Format output.
     * @returns {any} JSON-compatible value.
     */
    static toJSON(value)
    {
        return toJsonValue(value);
    }

    /**
     * Test whether bytes look like a WebM file.
     *
     * @param {Uint8Array|ArrayBuffer|DataView} input Candidate WebM bytes.
     * @returns {boolean} True when the EBML signature is present.
     */
    static isWebM(input)
    {
        try
        {
            return isWebM(toBytes(input));
        }
        catch
        {
            return false;
        }
    }

    /**
     * Emit targets for this format (canonical frozen enum).
     */
    static Output = Object.freeze({
        VIDEO: OUTPUT_VIDEO,
        RAW: OUTPUT_RAW,
        JSON: OUTPUT_JSON
    });

    static OUTPUT_WEBM_JSON = "webmJson";

    static id = "webm";

    static mediaTypes = Object.freeze([ "video" ]);

    static outputs = CjsFormat.defineOutputs({

        video: {  },

        webmJson: { role: "debug", probes: [ "webmJson", "raw" ] },

        raw: { role: "debug", default: true, passthrough: true }

    });

    static extensions = Object.freeze([ ".webm" ]);
}

export default CjsWebmFormat;
